/* Phase 5 · Visitor Management — End-of-day printable register (A4)
 * Called from the guard's "End-of-day print" button; auto-triggers window.print()
 * for hand-off at shift close. Includes visitor photos + vehicle photos inline.
 */
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { AuthImage } from "@/components/AuthMedia";

export default function VisitorEOD() {
  const [sp] = useSearchParams();
  const date = sp.get("date") || new Date().toISOString().slice(0, 10);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/visitors/eod", { params: { date } })
      .then(r => setData(r.data))
      .catch(() => setData({ visitors: [], summary: {} }));
  }, [date]);

  useEffect(() => {
    if (data && data.visitors.length > 0) {
      setTimeout(() => window.print(), 600);
    }
  }, [data]);

  const downloadCSV = async () => {
    const r = await fetch(`${API}/visitors/eod/csv?date=${date}`, { credentials: "include" });
    const blob = await r.blob();
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u; a.download = `visitors_${date}.csv`;
    a.click(); URL.revokeObjectURL(u);
  };

  if (!data) return <div className="p-6 text-sm text-[#7C8E9E]">Loading…</div>;

  return (
    <div data-testid="visitor-eod" style={{ background: "#F2ECE0", minHeight: "100vh" }}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: A4; margin: 12mm; }
          body { background: white !important; }
          .sheet { background: white !important; box-shadow: none !important; }
          tr { break-inside: avoid; }
        }
        .sheet { background: white; padding: 8mm; max-width: 210mm; margin: 4mm auto; }
      `}</style>

      <div className="no-print flex items-center gap-2 p-4 border-b bg-white">
        <button onClick={() => window.print()} data-testid="veod-print"
          className="px-4 py-2 rounded-full text-white text-sm font-semibold"
          style={{ background: "#1B3A5C" }}>
          Print
        </button>
        <button onClick={downloadCSV} data-testid="veod-csv"
          className="px-4 py-2 rounded-full text-xs font-semibold border border-[#1B3A5C]/20">
          Export CSV
        </button>
        <span className="text-xs text-[#7C8E9E] ml-auto">
          {data.visitors.length} visitors · {data.summary?.still_in || 0} still in · {data.summary?.with_vehicle || 0} with vehicle
        </span>
      </div>

      <div className="sheet">
        <div style={{ borderBottom: "2px solid #0B2340", paddingBottom: 6, marginBottom: 12 }}>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 18, fontWeight: 700, color: "#0B2340" }}>
            HAG Carpets Pvt. Ltd. · Mirzapur
          </div>
          <div style={{ fontSize: 11, color: "#4A627A" }}>
            Visitor Register · End of Day · {date}
          </div>
        </div>

        <div style={{ fontSize: 10, color: "#4A627A", marginBottom: 10 }}>
          <b>Total:</b> {data.summary?.total || 0}
          {" · "}<b>Checked out:</b> {data.summary?.checked_out || 0}
          {" · "}<b>Still in:</b> {data.summary?.still_in || 0}
          {" · "}<b>With vehicle:</b> {data.summary?.with_vehicle || 0}
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
          <thead>
            <tr style={{ background: "#FAF6F0", textAlign: "left" }}>
              <th style={th}>#</th>
              <th style={th}>In · Out</th>
              <th style={th}>Visitor</th>
              <th style={th}>Purpose · Host</th>
              <th style={th}>ID</th>
              <th style={th}>Vehicle</th>
              <th style={th}>Photos</th>
            </tr>
          </thead>
          <tbody>
            {data.visitors.map((v, i) => (
              <tr key={v.id} style={{ borderTop: "1px solid #1B3A5C22" }}>
                <td style={td}>{i + 1}</td>
                <td style={td}>
                  <div>{(v.in_time || "").slice(11, 16)}</div>
                  <div style={{ color: "#7C8E9E" }}>{v.out_time ? v.out_time.slice(11, 16) : "IN"}</div>
                </td>
                <td style={td}>
                  <div style={{ fontWeight: 600 }}>{v.name}</div>
                  <div style={{ color: "#7C8E9E", fontSize: 9 }}>{v.phone}</div>
                  <div style={{ fontFamily: "monospace", fontSize: 8, color: "#7C8E9E" }}>{v.visitor_no}</div>
                </td>
                <td style={td}>
                  <div>{v.purpose || "—"}</div>
                  <div style={{ color: "#7C8E9E", fontSize: 9 }}>{v.host_name || ""}</div>
                </td>
                <td style={td}>
                  <div>{v.id_type}</div>
                  <div style={{ fontFamily: "monospace", fontSize: 9 }}>{v.id_number}</div>
                </td>
                <td style={{ ...td, fontFamily: "monospace", textTransform: "uppercase" }}>
                  {v.vehicle_no || "—"}
                </td>
                <td style={td}>
                  <div style={{ display: "flex", gap: 4 }}>
                    {v.visitor_photo_file_id && (
                      <AuthImage fileId={v.visitor_photo_file_id}
                        alt="visitor"
                        className="object-cover rounded border"
                        style={{ width: 32, height: 32 }} />
                    )}
                    {v.vehicle_photo_file_id && (
                      <AuthImage fileId={v.vehicle_photo_file_id}
                        alt="vehicle"
                        className="object-cover rounded border"
                        style={{ width: 32, height: 32 }} />
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {data.visitors.length === 0 && (
              <tr>
                <td colSpan="7" style={{ ...td, textAlign: "center", color: "#7C8E9E", padding: 20 }}>
                  No visitors logged for {date}.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div style={{ marginTop: 30, display: "flex", justifyContent: "space-between", fontSize: 9, color: "#4A627A" }}>
          <div>
            <div style={{ marginBottom: 30 }}>Guard signature</div>
            <div style={{ borderTop: "1px solid #1B3A5C", width: 140 }}></div>
          </div>
          <div>
            <div style={{ marginBottom: 30 }}>Supervisor signature</div>
            <div style={{ borderTop: "1px solid #1B3A5C", width: 140 }}></div>
          </div>
        </div>
      </div>
    </div>
  );
}

const th = { padding: "6px 4px", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.5px", color: "#7C8E9E", borderBottom: "1px solid #1B3A5C22" };
const td = { padding: "6px 4px", verticalAlign: "top" };
